// What a season is, and what is said at the top of every view of one. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// A FINISHED SEASON NEVER GOES STALE, AND THIS FILE IS WHERE THE RULE LIVES
// ═══════════════════════════════════════════════════════════════════════════
//
// The design greys out any file older than 36 hours (`staleAfterHours`), and it was drawn against a feed that is
// rewritten every night. A FINISHED season's files are written once and never refreshed: under that rule every
// history view would read *Last knew* from the second day on, forever, with an amber dot and a dashed border on a
// page whose numbers cannot change. (RECONCILIATION-seasons.md, rule 1.) So:
//
//   a finished season     the kicker `2025-26 · REGULAR SEASON · FINAL` and the file's own date, `AS OF SAT 3 OCT,
//                         21:03 EDT`. No age, no stale state, no dot, no dashed border, however old the date is.
//   a season marked current   the design's rule: older than `FILE_STALE_AFTER_MS` is stale, and it says so with the
//                         amber dot, `LAST KNEW`, how long ago, and one sentence about when it stopped refreshing.
//
// THE SECOND HALF IS CONNECTED (P6-332, GN-5). The bot pulls the season being played nightly and marks its manifest
// `"current": true` (`scripts/backfill_hoops_history.py::build_season(current=True)`, run by
// `utils/hoops_season_pull.py`); a finished season's manifest carries no such key. This file reads the mark in one
// place, `isCurrent`, and the rule is `manifest.current === true`. The branch is tested with a season marked current
// and an old date, and a finished season with the same date is tested beside it.

import { UNKNOWN_AS_OF, instantOf } from '../freshness.js'
import { ageSeconds, ageText } from './time.js'
import { clock24, dateInZone, historySource, wdDayMon, zoneAbbreviation } from './historyFormat.js'

/** The design's `staleAfterHours` default: a CURRENT season's file older than this is stale. Never applied to a finished one. */
export const FILE_STALE_AFTER_MS = 36 * 3_600_000

/**
 * Is this season the one in progress?
 *
 * `entry` is one of `history.js::buildSeasons`'s entries, which keeps the season's manifest whole. The rule is the
 * single line below, and the producer writes the mark (see the header): a season with no `current` key is finished.
 */
export function isCurrent(entry) {
  return entry?.manifest?.current === true
}

/** `2025-26 · Regular season · Final`: the kicker alone, which is true of the season whether or not a file has arrived. */
export function kickerOf(season, entry) {
  return `${season} · Regular season · ${isCurrent(entry) ? 'In progress' : 'Final'}`
}

/**
 * What is said above a view: the kicker, and the line that dates the file.
 *
 * `asOf` and `source` are the file's own (`as_of`, `source`) and `nowMs` is the page's clock. `timeZone` is the viewer's
 * (`undefined` meaning the device's); production never passes one.
 *
 * Returns `{ kicker, age, stale, staleNote, finished }`. `stale` is true ONLY for a season marked current whose file
 * is older than `FILE_STALE_AFTER_MS`; `staleNote` is `null` otherwise.
 */
export function headModel({ season, entry, asOf, source, nowMs, timeZone }) {
  const finished = !isCurrent(entry)
  const kicker = kickerOf(season, entry)
  const name = historySource(source)
  const stamp = instantOf(asOf)

  // An unreadable stamp is said in words, never printed as a date it is not (`freshness.js`'s rule).
  if (stamp === null) {
    return { kicker, age: [name, UNKNOWN_AS_OF].filter(Boolean).join(' · '), stale: false, staleNote: null, finished }
  }

  const day = wdDayMon(dateInZone(stamp, timeZone))
  const time = clock24(stamp, timeZone)
  const zone = zoneAbbreviation(stamp, timeZone)
  const when = `${day}, ${time} ${zone}`

  if (finished) {
    return { kicker, age: [name, `As of ${when}`].filter(Boolean).join(' · '), stale: false, staleNote: null, finished }
  }

  const seconds = ageSeconds(stamp, nowMs)
  const stale = nowMs - stamp > FILE_STALE_AFTER_MS
  return {
    kicker,
    age: [name, `${stale ? 'Last knew' : 'As of'} ${when}`, `${ageText(seconds)} ago`].filter(Boolean).join(' · '),
    stale,
    staleNote: stale ? `This file stopped refreshing on ${day} at ${time}. Everything here is as of then.` : null,
    finished,
  }
}
